import { useId } from "react";

export default function AboutIconMini({
  size = 28,
  active = false,
  title = "About Us",
  ...rest
}) {
  const uid = useId();
  const id = (name) => `${uid}-${name}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label={title}
      style={{ display: "block" }}
      data-preserve
      {...rest}
    >
      <title>{title}</title>

      <defs>
        {/* Outer rim aura */}
        <radialGradient id={id("rimGlow")} cx="50%" cy="50%" r="50%">
          <stop offset="60%" stopColor="#00081a" stopOpacity="0" />
          <stop offset="90%" stopColor="#FF6DA8" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#5AA8FF" stopOpacity="0.6" />
        </radialGradient>

        {/* Rim stroke gradient */}
        <linearGradient
          id={id("rimStroke")}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#FF6DA8" />
          <stop offset="50%" stopColor="#a95cff" />
          <stop offset="100%" stopColor="#5AA8FF" />
        </linearGradient>

        {/* Inner face (dark / deep) */}
        <radialGradient id={id("faceFill")} cx="50%" cy="45%" r="60%">
          <stop offset="0%" stopColor="#0d1020" />
          <stop offset="70%" stopColor="#05060d" />
          <stop offset="100%" stopColor="#000000" />
        </radialGradient>

        {/* Rim highlight (fake glass shine) */}
        <radialGradient id={id("rimHighlight")} cx="50%" cy="35%" r="70%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="60%" stopColor="#ffffff" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>

        {/* Neon gradient for the question mark */}
        <linearGradient
          id={id("iconStroke")}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#FF6DA8" />
          <stop offset="40%" stopColor="#ff4fa4" />
          <stop offset="60%" stopColor="#7b4dff" />
          <stop offset="100%" stopColor="#5AA8FF" />
        </linearGradient>

        {/* Glow around question mark */}
        <filter
          id={id("iconGlow")}
          x="-50%"
          y="-50%"
          width="200%"
          height="200%"
        >
          <feGaussianBlur
            in="SourceGraphic"
            stdDeviation="2.2"
            result="blur1"
          />
          <feColorMatrix
            in="blur1"
            type="matrix"
            values="
              1 0 0 0 0
              0 1 0 0 0
              0 0 1 0 0
              0 0 0 1 0
            "
          />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Big aura when active */}
        <filter
          id={id("activeAura")}
          x="-80%"
          y="-80%"
          width="260%"
          height="260%"
        >
          <feGaussianBlur stdDeviation="6" result="b1" />
          <feMerge>
            <feMergeNode in="b1" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Tiny sparkle blur */}
        <filter id={id("spark")} x="-200%" y="-200%" width="400%" height="400%">
          <feGaussianBlur stdDeviation="0.6" />
        </filter>
      </defs>

      {/* === COIN BODY === */}
      <g filter={active ? `url(#${id("activeAura")})` : undefined}>
        {/* Neon aura ring behind rim */}
        <circle cx="32" cy="32" r="30" fill={`url(#${id("rimGlow")})`} />

        {/* Colored rim stroke */}
        <circle
          cx="32"
          cy="32"
          r="30"
          fill="none"
          stroke={`url(#${id("rimStroke")})`}
          strokeWidth="4"
        />

        {/* Gloss highlight toward top */}
        <circle
          cx="32"
          cy="24"
          r="26"
          fill={`url(#${id("rimHighlight")})`}
          opacity="0.4"
        />

        {/* Dark inner face */}
        <circle cx="32" cy="32" r="24" fill={`url(#${id("faceFill")})`} />

        {/* Inner subtle bevel */}
        <circle
          cx="32"
          cy="32"
          r="24"
          fill="none"
          stroke="#ffffff"
          strokeOpacity="0.18"
          strokeWidth="1.25"
        />
      </g>

      {/* === QUESTION MARK ICON === */}
      <g
        filter={`url(#${id("iconGlow")})`}
        stroke={`url(#${id("iconStroke")})`}
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        {/* Curved hook of the question mark.
           This is drawn large so it reads even at 28px total icon.
           Starts near top-middle, curves down, then angles toward center.
        */}
        <path
          d="
            M 30 22
            Q 34 20 37 22
            Q 40 24 40 28
            Q 40 31 38 33
            Q 36 35 34 36.5
            Q 32 38 32 40
          "
        />

        {/* Dot of the question mark */}
        <circle
          cx="32"
          cy="44"
          r="2"
          fill={`url(#${id("iconStroke")})`}
          stroke="none"
        />
      </g>

      {/* Sparkles on rim (same vibe as PortalIconMini) */}
      <g opacity="0.9">
        <circle
          cx="20"
          cy="22"
          r="0.8"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="44"
          cy="18"
          r="0.6"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="48"
          cy="36"
          r="0.7"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="18"
          cy="40"
          r="0.5"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
      </g>
    </svg>
  );
}
